import { FC, Fragment, useState } from 'react';
import {
	Button,
	Col,
	Divider,
	Form,
	Input,
	InputNumber,
	Row,
	Table,
} from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { FormAlert, useForm } from '@/libs/antd/form';
import {
	addFRMOfficeObservationUnit,
	deleteFRMOfficeObservationUnit,
	updateFRMOfficeObservationUnit,
} from '@/libs/fleet-api/services/visit-reports';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import rule from '@/libs/antd/rules';
import { useMutation } from '@tanstack/react-query';
import { useFRMVisitReportPageContext } from './VisitReportOfficePage';
import { OfficeObservationUnit } from '@/libs/fleet-api/services/types';
import { useModals } from '@/components/modals/ModalProvider';

type VisitReportUnitForm = {
	input?: OfficeObservationUnit<'payload'>;
	lists: OfficeObservationUnit<'payload'>[];
};

const FRMVisitReportUnits: FC = () => {
	const modalAPI = useModals();
	const { surveyInformation, officeObservation } =
		useFRMVisitReportPageContext();
	const { technicalAspect } = officeObservation ?? {};
	const { form, value, fieldName } = useForm<VisitReportUnitForm>();
	const [modal, setModal] = useState<
		{ type: 'add' } | { type: 'edit'; index: number } | null
	>(null);

	const addFRMOfficeObservationUnitMutation = useMutation({
		mutationFn: addFRMOfficeObservationUnit,
	});

	const updateFRMOfficeObservationUnitMutation = useMutation({
		mutationFn: updateFRMOfficeObservationUnit,
	});

	const deleteFRMOfficeObservationUnitMutation = useMutation({
		mutationFn: deleteFRMOfficeObservationUnit,
	});

	return (
		<Form.Provider>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				initialValues={
					{
						lists: technicalAspect?.units ?? [],
					} as VisitReportUnitForm
				}
				onFinish={async ({ input, lists }) => {
					if (modal?.type === 'edit') {
						form.setFieldsValue({
							lists: lists.map((list, index) => {
								return index === modal.index ? input! : list;
							}),
						});
						await updateFRMOfficeObservationUnitMutation.mutateAsync({
							...input!,
							surveyId: surveyInformation!.id!,
						});
					} else {
						const res = await addFRMOfficeObservationUnitMutation.mutateAsync({
							...input!,
							surveyId: surveyInformation!.id!,
						});
						form.setFieldsValue({
							lists: [
								...lists,
								{
									...input!,
									id: res.data,
								},
							],
						});
					}
					setModal(null);
				}}
				style={{ marginBottom: 30 }}
			>
				<Form.Item
					name={fieldName(['input'])}
					noStyle
				/>
				<Form.Item
					name={fieldName(['lists'])}
					noStyle
				/>
				<Form.Item shouldUpdate>
					{({ getFieldsValue, setFieldValue }) => {
						const values = getFieldsValue() as VisitReportUnitForm;
						const dataSource =
							values?.lists?.map((list, key?) => {
								return { ...list, key };
							}) ?? [];

						return (
							<Fragment>
								<Row style={{ display: 'flex', justifyContent: 'end' }}>
									<Button
										style={{
											color: '#1557BC',
											borderRadius: 50,
											fontSize: 12,
											height: 24,
											borderColor: '#1557BC',
											fontWeight: 700,
											marginBottom: 20,
										}}
										loading={
											addFRMOfficeObservationUnitMutation.isPending ||
											updateFRMOfficeObservationUnitMutation.isPending
										}
										onClick={() => setModal({ type: 'add' })}
										icon={'+'}
									>
										Add Another
									</Button>
								</Row>
								<Table
									bordered
									pagination={false}
									dataSource={dataSource}
									columns={[
										{
											title: (
												<span
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													Action
												</span>
											),
											render: (_, record, index) => {
												return (
													<Row
														gutter={[40, 20]}
														style={{
															display: 'flex',
															flexWrap: 'nowrap',
															justifyContent: 'center',
															paddingLeft: 20,
															paddingRight: 20,
														}}
													>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={(e) => {
																	e.preventDefault();
																	setFieldValue(fieldName(['input']), record);
																	setModal({
																		type: 'edit',
																		index,
																	});
																}}
															>
																<EditOutlined />
															</a>
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
															}}
														>
															<Divider type="vertical" />
														</div>
														<div
															style={{
																display: 'flex',
																flexWrap: 'nowrap',
																justifyContent: 'center',
																width: 'fit-content',
															}}
														>
															<a
																style={{ display: 'block' }}
																onClick={async (e) => {
																	e.preventDefault();
																	const confirmDelete =
																		await modalAPI.modal.confirm({
																			title: 'delete this data?',
																		});

																	if (confirmDelete) {
																		await deleteFRMOfficeObservationUnitMutation.mutateAsync(
																			record.id!,
																		);
																		form.setFieldsValue({
																			lists: values.lists.filter(
																				(_, listIndex) => listIndex !== index,
																			),
																		});
																	}
																}}
															>
																<DeleteOutlined />
															</a>
														</div>
													</Row>
												);
											},
											width: 1,
										},
										{
											title: 'Jumlah Unit',
											render: (_, record) => record.unitAmount ?? '-',
										},
										{
											title: 'Detail Merk Unit',
											render: (_, record) => record.unitBrandDetail ?? '-',
										},
									]}
								/>
								<FormAlert
									alerts={!value?.lists?.length ? ['Unit wajib diisi'] : []}
								/>
								<FleetModal
									open={Boolean(modal)}
									width={940}
									closeIcon={false}
									keyboard={false}
									maskClosable={false}
									buttonPosition="end"
									afterClose={() => {
										form.resetFields([fieldName('input')]);
									}}
									onCancel={() => {
										setModal(null);
									}}
									onOk={() => {
										form.submit();
									}}
								>
									<FleetTitle>Add Jumlah Unit</FleetTitle>
									<Row gutter={[40, 20]}>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'id'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'surveyId'])}
												noStyle
											/>
											<Form.Item
												name={fieldName(['input', 'unitAmount'])}
												label="Jumlah Unit"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<InputNumber min={0} />
											</Form.Item>
										</Col>
										<Col span={12}>
											<Form.Item
												name={fieldName(['input', 'unitBrandDetail'])}
												label="Detail Merk Unit"
												labelCol={{ span: 10 }}
												rules={[rule.required()]}
											>
												<Input />
											</Form.Item>
										</Col>
									</Row>
									<Divider />
								</FleetModal>
							</Fragment>
						);
					}}
				</Form.Item>
			</Form>
		</Form.Provider>
	);
};

export default FRMVisitReportUnits;
